import * as React from "react";
import { memo } from "react";
import { ArrowUpDown, ChevronDown } from "lucide-react";
import {
  shallowEqual,
  useListSortContext,
  useTranslate,
} from "ra-core";
import { useIsMobile } from "@/hooks/use-mobile";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

export const CompanySortButton = () => {
  const { sort, setSort } = useListSortContext();
  const translate = useTranslate();
  const isMobile = useIsMobile();
  const [open, setOpen] = React.useState(false);

  const fieldLabels: Record<string, string> = {
    name: translate("simplecrm.companies.name", { _: "Nome" }),
    created_at: translate("simplecrm.companies.created_at", { _: "Data de criação" }),
    nb_contacts: translate("simplecrm.companies.nb_contacts", { _: "Número de contatos" }),
  };

  const sortOptions = [
    { field: "name", order: "ASC" as const },
    { field: "name", order: "DESC" as const },
    { field: "created_at", order: "ASC" as const },
    { field: "created_at", order: "DESC" as const },
    { field: "nb_contacts", order: "ASC" as const },
    { field: "nb_contacts", order: "DESC" as const },
  ];

  const handleChangeSort = (field: string, order: "ASC" | "DESC") => {
    setSort({ field, order });
    setOpen(false);
  };

  const fieldLabel = fieldLabels[sort.field] || sort.field;
  const buttonLabel = translate("ra.sort.sort_by", {
    field: fieldLabel,
    field_lower_first:
      typeof fieldLabel === "string"
        ? fieldLabel.charAt(0).toLowerCase() + fieldLabel.slice(1)
        : undefined,
    order: translate(`ra.sort.${sort.order}`),
    _: "Ordenar por",
  });

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      {isMobile ? (
        <TooltipProvider>
          <Tooltip>
            <DropdownMenuTrigger asChild>
              <TooltipTrigger asChild>
                <Button
                  variant="outline"
                  size="icon"
                  aria-label={buttonLabel}
                >
                  <ArrowUpDown className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
            </DropdownMenuTrigger>
            <TooltipContent>
              <p>{buttonLabel}</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      ) : (
        <DropdownMenuTrigger asChild>
          <Button variant="outline" size="sm" className="h-9">
            <ArrowUpDown className="h-4 w-4" />
            <span className="ml-2">{buttonLabel}</span>
            <ChevronDown className="ml-2 h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
      )}
      <DropdownMenuContent align="start">
        {sortOptions.map((option) => (
          <DropdownMenuItem
            key={`${option.field}-${option.order}`}
            onClick={() => handleChangeSort(option.field, option.order)}
          >
            {fieldLabels[option.field]}{" "}
            {translate(`ra.sort.${option.order}`)}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

